import { Navigate, Route, Routes } from "react-router-dom";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { Layout } from "./components/Layout";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import GettingStarted from "./pages/GettingStarted";
import AccountPage from "./pages/account/AccountPage";
import FolderManager from "./pages/folders/FolderManager";
import FolderView from "./pages/folders/FolderView";
import ReportList from "./pages/reports/ReportList";
import ReportSettings from "./pages/reports/ReportSettings";
import ParamBuilder from "./pages/reports/ParamBuilder";
import ReportDesigner from "./pages/designer/ReportDesigner";
import EndpointConfig from "./pages/endpoint/EndpointConfig";
import FieldMappingTable from "./pages/mapping/FieldMappingTable";
import ApiKeyManager from "./pages/apikeys/ApiKeyManager";
import UserManager from "./pages/users/UserManager";
import { PlatformOverview, PlatformTenants, PlatformEmailDefaults, PlatformLicenses } from "./pages/admin/TenantAdmin";
import TenantDetail from "./pages/admin/TenantDetail";
import IntegrationSample from "./pages/integration/IntegrationSample";
import EmailConfig from "./pages/settings/EmailConfig";
import Branding from "./pages/settings/Branding";
import ScheduleManager from "./pages/schedules/ScheduleManager";
import Billing from "./pages/billing/Billing";
import Revenue from "./pages/admin/Revenue";
import SetupWizard from "./pages/setup/SetupWizard";
import LicensePage from "./pages/license/LicensePage";
import LicenseGate from "./pages/license/LicenseGate";
import { useDeployment } from "./deployment/DeploymentContext";

function Authenticated({ children }: { children: React.ReactNode }) {
  const { blocked } = useDeployment();
  return (
    <ProtectedRoute>
      {/* On-prem with an expired/missing license the app is replaced by the renew-license screen. */}
      {blocked ? <LicenseGate /> : <Layout>{children}</Layout>}
    </ProtectedRoute>
  );
}

/** Features that only exist on the hosted SaaS (signup, billing, platform administration). */
function SaasOnly({ children }: { children: React.ReactNode }) {
  const { isOnPrem } = useDeployment();
  return isOnPrem ? <Navigate to="/dashboard" replace /> : <>{children}</>;
}

function PlatformRoute({ children }: { children: React.ReactNode }) {
  return (
    <SaasOnly>
      <ProtectedRoute requireRole={["PLATFORM_SUPERADMIN"]} requireTenant={false}>
        <Layout>{children}</Layout>
      </ProtectedRoute>
    </SaasOnly>
  );
}

export function AppRouter() {
  const { status, loading, isOnPrem } = useDeployment();

  if (loading) {
    return <div className="setup-shell"><div className="setup-loading">Loading…</div></div>;
  }

  // A brand-new on-prem install has no organisation yet: the only thing to do is the setup wizard.
  if (isOnPrem && status?.needsSetup) {
    return (
      <Routes>
        <Route path="/setup" element={<SetupWizard />} />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SaasOnly><Signup /></SaasOnly>} />
      <Route path="/setup" element={<Navigate to="/login" replace />} />
      <Route path="/settings/license" element={<Authenticated><LicensePage /></Authenticated>} />

      <Route path="/dashboard" element={<Authenticated><Dashboard /></Authenticated>} />
      <Route path="/getting-started" element={<Authenticated><GettingStarted /></Authenticated>} />
      <Route path="/account" element={<Authenticated><AccountPage /></Authenticated>} />

      <Route path="/folders" element={<Authenticated><FolderManager /></Authenticated>} />
      <Route path="/folders/:folderId" element={<Authenticated><FolderView /></Authenticated>} />

      <Route path="/reports" element={<Authenticated><ReportList /></Authenticated>} />
      <Route path="/reports/:reportId/settings" element={<Authenticated><ReportSettings /></Authenticated>} />
      <Route path="/reports/:reportId/params" element={<Authenticated><ParamBuilder /></Authenticated>} />
      <Route path="/reports/:reportId/designer" element={<Authenticated><ReportDesigner /></Authenticated>} />
      <Route path="/reports/:reportId/endpoint" element={<Authenticated><EndpointConfig /></Authenticated>} />
      <Route path="/reports/:reportId/mapping" element={<Authenticated><FieldMappingTable /></Authenticated>} />

      <Route path="/schedules" element={<Authenticated><ScheduleManager /></Authenticated>} />
      <Route path="/billing" element={<SaasOnly><Authenticated><Billing /></Authenticated></SaasOnly>} />
      <Route path="/settings/email" element={<Authenticated><EmailConfig /></Authenticated>} />
      <Route path="/settings/branding" element={<Authenticated><Branding /></Authenticated>} />
      <Route path="/api-keys" element={<Authenticated><ApiKeyManager /></Authenticated>} />
      <Route path="/users" element={<Authenticated><UserManager /></Authenticated>} />
      <Route path="/admin/overview" element={<PlatformRoute><PlatformOverview /></PlatformRoute>} />
      <Route path="/admin/tenants" element={<PlatformRoute><PlatformTenants /></PlatformRoute>} />
      <Route path="/admin/tenants/:tenantId" element={<PlatformRoute><TenantDetail /></PlatformRoute>} />
      <Route path="/admin/email" element={<PlatformRoute><PlatformEmailDefaults /></PlatformRoute>} />
      <Route path="/admin/licenses" element={<PlatformRoute><PlatformLicenses /></PlatformRoute>} />
      <Route path="/admin/revenue" element={<PlatformRoute><Revenue /></PlatformRoute>} />
      <Route path="/integration-sample" element={<Authenticated><IntegrationSample /></Authenticated>} />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
